customElements.get('parallax-section') ||
  customElements.define(
    'parallax-section',
    class extends HTMLElement {
      constructor() {
        super();
        this.images = this.querySelectorAll('.wt-parallax__img');
        this._mobileMq = window.matchMedia('(max-width: 900px)');
        this._onScroll = null;
        this._onOrientation = null;
        this._ticking = false;
        this._rotationViewport = 0;
        this._headerOffset = null;
        this._paneHeight = null;
        this._stickMode = null;
        this._content = null;
        this._root = null;
        this._spacer = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        if (this._onScroll) {
          document.removeEventListener('scroll', this._onScroll);
          this._onScroll = null;
        }

        if (this._onOrientation) {
          window.removeEventListener('orientationchange', this._onOrientation);
          this._onOrientation = null;
        }

        this.teardownMobileStick();
      }

      getHeaderOffset() {
        if (this._headerOffset == null) {
          const raw = getComputedStyle(document.documentElement).getPropertyValue('--plz-header-offset');
          const parsed = Number.parseFloat(raw);
          this._headerOffset = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 72;
        }
        return this._headerOffset;
      }

      getPaneHeight() {
        if (this._paneHeight == null && this._content) {
          const style = getComputedStyle(this._content);
          const parsed = Number.parseFloat(style.height);
          this._paneHeight =
            Number.isFinite(parsed) && parsed > 0
              ? Math.round(parsed)
              : Math.round(window.innerHeight - this.getHeaderOffset());
        }
        return this._paneHeight || 0;
      }

      resetStickMetrics() {
        this._headerOffset = null;
        this._paneHeight = null;
        this._rotationViewport = 0;
        this._stickMode = null;
      }

      setupMobileStick() {
        if (!this._mobileMq.matches || this._spacer) return;

        this._root = this.closest('.wt-parallax');
        this._content = this.querySelector('.wt-parallax__content');
        if (!this._root || !this._content) return;

        this._spacer = document.createElement('div');
        this._spacer.className = 'wt-parallax__pin-spacer';
        this._spacer.setAttribute('aria-hidden', 'true');
        this._content.insertAdjacentElement('afterend', this._spacer);
        this._root.classList.add('wt-parallax--fixed-stick');
      }

      teardownMobileStick() {
        if (this._root) {
          this._root.classList.remove('wt-parallax--fixed-stick');
        }

        this.setStickMode('flow', true);

        if (this._spacer && this._spacer.parentNode) {
          this._spacer.parentNode.removeChild(this._spacer);
        }

        this._spacer = null;
        this._content = null;
        this._root = null;
        this._stickMode = null;
      }

      getStickMode(sectionTop, sectionBottom, offset, paneH) {
        if (sectionBottom <= offset) return 'idle';
        if (sectionTop > offset) return 'flow';
        if (sectionBottom > offset + paneH) return 'pinned';
        return 'exit';
      }

      setStickMode(mode, force = false) {
        if (!force && mode === this._stickMode) return;
        if (!this._content || !this._spacer) return;

        const offset = this.getHeaderOffset();
        const paneH = this.getPaneHeight();
        const exitTop = mode === 'exit' ? this.offsetHeight - paneH : null;

        this._content.classList.remove('wt-parallax__content--is-fixed', 'wt-parallax__content--is-exit');
        this._content.style.removeProperty('top');
        this._content.style.removeProperty('transform');

        if (mode === 'pinned') {
          this._spacer.style.height = paneH + 'px';
          this._content.classList.add('wt-parallax__content--is-fixed');
          this._content.style.top = offset + 'px';
        } else if (mode === 'exit') {
          this._spacer.style.height = paneH + 'px';
          this._content.classList.add('wt-parallax__content--is-exit');
          this._content.style.top = `${exitTop}px`;
        } else {
          this._spacer.style.height = '0px';
        }

        this._stickMode = mode;
      }

      updateMobileStick() {
        if (!this._mobileMq.matches || !this._content || !this._spacer) return;

        const offset = this.getHeaderOffset();
        const paneH = this.getPaneHeight();
        const rect = this.getBoundingClientRect();
        const mode = this.getStickMode(rect.top, rect.bottom, offset, paneH);

        this.setStickMode(mode);
      }

      getRotationViewport() {
        if (!this._rotationViewport) {
          this._rotationViewport = window.innerHeight;
        }
        return this._rotationViewport;
      }

      decorateImages() {
        this.querySelectorAll('.wt-parallax__gallery__item').forEach((item, index) => {
          const image = item.querySelector('.wt-parallax__img');
          if (!image) return;
          image.classList.add(`wt-parallax__img--${index % 2 === 0 ? 'odd' : 'even'}`);
        });

        this.images.forEach((image) => {
          if (image.complete) return;
          image.addEventListener(
            'load',
            () => {
              this._stickMode = null;
              this.updateMobileStick();
            },
            { once: true }
          );
        });
      }

      bindScroll() {
        const root = this.closest('.wt-parallax') || this;
        const { images } = this;
        const maxRotation = parseInt(this.dataset.rotation, 10) || 30;

        const updateRotation = () => {
          this._ticking = false;

          if (!images.length) return;

          const rootRect = root.getBoundingClientRect();
          const windowHeight = this.getRotationViewport();

          if (rootRect.bottom < 0 || rootRect.top > windowHeight) return;

          images.forEach((image) => {
            const imageRect = image.getBoundingClientRect();

            if (imageRect.top < windowHeight) {
              const visiblePercent = ((windowHeight - imageRect.top) / windowHeight) * 100;
              let rotationDegree = Math.min((visiblePercent / 100) * maxRotation, maxRotation);

              if (image.classList.contains('wt-parallax__img--odd')) {
                rotationDegree *= -1;
              }

              image.style.transform = `translate3d(0, 0, 0) rotate(${rotationDegree}deg)`;
            }
          });
        };

        this._onScroll = () => {
          this.updateMobileStick();

          if (this._ticking) return;
          this._ticking = true;
          window.requestAnimationFrame(updateRotation);
        };

        document.addEventListener('scroll', this._onScroll, { passive: true });
      }

      init() {
        this.decorateImages();
        this.setupMobileStick();
        this.bindScroll();
        this.updateMobileStick();

        this._onOrientation = () => {
          this.resetStickMetrics();
          this.updateMobileStick();
        };

        window.addEventListener('orientationchange', this._onOrientation, { passive: true });

        this._mobileMq.addEventListener('change', () => {
          if (this._mobileMq.matches) {
            this.setupMobileStick();
          } else {
            this.teardownMobileStick();
            this.resetStickMetrics();
          }
          this.updateMobileStick();
        });
      }
    }
  );
